import React, {lazy, useState} from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
    useAuthContext,
    useModalsContext,
    useStoreConfigContext,
    useWishListsContext,
    useCartContext,
} from '/src/providers';
import {ButtonTop, useForm} from '/src/components';
import * as styles from './styles.scss';

const Menu = lazy(() => import(/* webpackChunkName: "menu-mobile" */ './Menu'));

const Mobile = () => {
    const {
        storeConfig: {
            header_logo_src,
            logo_alt,
            header_mobile_logo_src
        },
        translate,
        formatPrice,
    } = useStoreConfigContext();
    const { isAuth, logout, isLoadingAuth } = useAuthContext();
    const { wishLists } = useWishListsContext();
    const { showModal, MODALS_TYPES } = useModalsContext();
    const {
        cart: {
            prices: { grand_total } = {},
            total_quantity
        },
        isLoadingCart
    } = useCartContext();
    const navigate = useNavigate();
    const [isOpen, setIsOpen] = useState(false);
    const isDisabled = isLoadingAuth || isLoadingCart;

    const handleShowAccount = () => showModal(MODALS_TYPES.ACCOUNT);
    const handleShowCart = () => showModal(MODALS_TYPES.CART);

    const totalItemsCount = wishLists?.reduce((accumulator, { items_count }) => accumulator + items_count, 0);
    const { data, handleChange } = useForm({ search: '' });

    const handleSearch = e => {
        e.preventDefault();

        const queryParams = new URLSearchParams();

        queryParams.set('search', data.search);

        navigate({
            pathname: '/search',
            search: data.search ? `?${queryParams.toString()}` : ''
        });
    };

    return (
        <>
            <ButtonTop
                className={styles.open}
                onClick={() => setIsOpen(true)}
                aria-label="Menu"
            />
            <div className={`${styles.menu}${isOpen ? ` ${styles.active}` : ''}`}>
                <div className={styles.head}>
                    <Link to="/" className={styles.logo}>
                        <img src={header_logo_src} alt={logo_alt} />
                    </Link>
                    <Link to="/" className={styles.mobile}>
                        <img src={`${header_mobile_logo_src}?d=80x60`} alt={logo_alt} />
                    </Link>
                    <ButtonTop
                        className={styles.close}
                        onClick={() => setIsOpen(false)}
                        aria-label="Close"
                    />
                </div>
                <div className={styles.body}>
                    <div
                        className={styles.cart}
                        onClick={handleShowCart}
                    >
                        <div className={styles.count}>
                            <ButtonTop
                                className={styles.basket}
                                aria-label="Basket"
                            />
                            <div className={styles.info}>
                                <div className={styles.name}>
                                    {translate('Basket')}
                                </div>
                                <div className={styles.total}>
                                    {translate('%1 product in basket', total_quantity || 0)}
                                </div>
                            </div>
                        </div>
                        <div className={styles.price}>
                            {!!grand_total && formatPrice(grand_total)}
                        </div>
                    </div>
                    <Menu />
                    <div className={styles.list}>
                        <button
                            className={styles.wish}
                            onClick={
                                isAuth
                                    ? () => { navigate('/customer/wish-lists'); setIsOpen(false); }
                                    : handleShowAccount
                            }
                            disabled={isDisabled}
                        >
                            {translate('Favorite products')}
                        </button>
                        {
                            isAuth &&
                            <div className={styles.items}>{`${totalItemsCount} ${translate('Items')}`}</div>
                        }
                    </div>
                    <button
                        className={styles.account}
                        onClick={
                            isAuth
                                ? () => { navigate('/customer'); setIsOpen(false); }
                                : handleShowAccount
                        }
                        disabled={isDisabled}
                    >
                        {translate('My account')}
                    </button>
                    {
                        isAuth &&
                        <div className={styles.lg}>
                            <button
                                className={styles.logout}
                                onClick={logout}
                                disabled={isDisabled}
                            >
                                {translate('Logout')}
                            </button>
                        </div>
                    }
                    <div className={styles.title}>
                        {translate('Search products')}
                    </div>
                    <div className={styles.wrap}>
                        <form
                            className={styles.form}
                            onSubmit={handleSearch}
                        >
                            <input
                                className={styles.input}
                                placeholder={translate('Start your healthy eating journey here...')}
                                type="text"
                                name="search"
                                value={data.search}
                                onChange={handleChange}
                                autoComplete="off"
                                aria-label="Search"
                            />
                            <ButtonTop
                                className={styles.button}
                                aria-label="Search"
                            />
                        </form>
                    </div>
                    <div className={styles.title}>
                        {translate('Our support')}
                    </div>
                    <a
                        className={styles.email}
                        href="mailto:support@buywholefoodsonline.co.uk"
                    >
                        support@buywholefoodsonline.co.uk
                    </a>
                </div>
            </div>
        </>
    )
};


export default Mobile;
